import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import { createApiClient } from '../api/client';
import { useAccessToken } from '../auth/useAuth';
import { actingFirmId, actingFirmQuery, useActingFirmId } from './advisorActingFirm';
import type { AdvisorClientsResponse } from './types';

/** The React Query key for `GET /advisor/clients`. Exported so no consumer has to retype
 *  the string — `WorkspaceProvider`'s reset predicate reads it too. */
export const ADVISOR_CLIENTS_QUERY_KEY = ['advisor-clients'] as const;

/**
 * Matches `useAdvisorFirm.ts`'s own staleTime, for the same reason its comment gives: this
 * answer changes only when a family connects or disconnects the firm, which is not
 * something that happens mid-glance, and React Query's 30s default plus
 * `refetchOnWindowFocus` would otherwise refire a server-side fan-out on every alt-tab
 * back into the browser.
 */
const ADVISOR_CLIENTS_STALE_TIME_MS = 5 * 60_000;

/**
 * Which of the caller's workspaces are clients of THIS firm, and how many of them there are.
 * The advisor portal's Clients page's one read, also read by the firm admin's "Your firm"
 * page (steward-app#1361) for its Active clients list.
 *
 * ⚠️ **There is no longer a second set** (steward-app#1021). This sentence used to end "and
 * which it could not place", for the `unverified` array the payload carried while the façade
 * answered the question with a read PER CANDIDATE that could fail. It answers it once now,
 * from the firm-side engagement Query, and a failure of that read is an ERROR rather than a
 * household this request is unsure about.
 *
 * Backed by `GET /advisor/clients` (docs/25 §2/§6). One request for the whole grid rather
 * than a `GET /advisor/connections` per card — that is steward-app#637's win, and it is
 * unchanged here.
 *
 * ⚠️ **Fetched by leaf pages only (the Clients page and "Your firm") and by NOTHING mounted
 * app-wide** (steward-app#661).
 * This is the one advisor read whose server-side cost scales with a firm's roster: the
 * façade answers it by reading each of the caller's own client workspaces. #637 folded it
 * into `GET /advisor/firms`, which `Layout.tsx` fetches once per session for every
 * authenticated user to decide whether to render the `/advisor` nav — so an advisor's nav
 * gate quietly became N workspace reads, worst exactly when a firm had a real roster. Keep
 * this hook where it is: mounting it from a layout, a provider, or a route guard would put
 * the cost straight back. `AdvisorClients.test.tsx` asserts the caller list structurally.
 *
 * ⚠️ **AUTHORITATIVE for the roster since steward-app#1036 step 3b, where it used to be a
 * caption JOIN — and the two differ in what a FAILURE costs, which is why this hook's
 * shape changed with it.** It used to return a bare `Map` and swallow everything: no error
 * surface at all, on the argument that a failed fetch costs one caption. That argument was
 * correct while the roster came from `/me` alone. It stops being correct the moment the
 * payload decides which families are listed, because the same swallow then renders an
 * advisor's whole book as "No clients have connected yet" — a confident, wrong, and
 * completely silent answer, which is the one thing steward-app#1036 forbids
 * (#804's soft-filter fallback is rejected in the other direction for the same reason:
 * never degrade to the unfiltered list). So this returns the FULL query result, exactly as
 * `useAdvisorSummary` does and for the same reason, and the page branches on it through
 * `queryDisplayState`.
 *
 * **What that leaves for the page to get right**, since this hook no longer decides it:
 * a pending or failed query must not render a grid at all (`AdvisorClients.tsx`'s branch),
 * and the intersection it feeds must never be reached with `data === undefined`.
 *
 * ⚠️ **An ERRORED query ignores `staleTime` entirely, so it takes THREE options to stop
 * one refiring — `retry: false` is not close to enough.** `Query.isStaleByTime` returns
 * `true` unconditionally when `state.data === undefined` (query-core `query.js`, first line
 * of the method), and a query that has never succeeded has no data. So every "is it stale?"
 * gate answers yes forever, and each refetch trigger has to be closed by name:
 *
 *   - `retryOnMount: false` closes MOUNT. `shouldFetchOnMount` reaches `refetchOnMount`
 *     only via `data !== undefined`; the errored path runs through `shouldLoadOnMount`,
 *     which is the one place `retryOnMount` is consulted (`queryObserver.js`).
 *   - `refetchOnWindowFocus: false` and `refetchOnReconnect: false` close FOCUS and
 *     RECONNECT. These go through `shouldFetchOn`, which never looks at `retryOnMount` at
 *     all — it checks `isStale`, which is the unconditional `true` above. `main.tsx` sets
 *     only `staleTime` and `retry` on the QueryClient, so both were at their default of
 *     `true`: an earlier draft of this hook had `retryOnMount` alone, and every alt-tab back
 *     into the browser refired the whole fan-out anyway. Caught in review of #661.
 *
 * That matters because the failure that makes this query fail is usually the façade's
 * fan-out degrading (an appcore brownout spending the whole 5s budget, or a 504) — exactly
 * when repeating it on every focus, reconnect, and bounce between `/advisor` and
 * `/advisor/clients/prospects` is the worst available behaviour. **All three are KEPT even though a
 * failure now costs the whole page**, because the page offers an explicit Retry instead:
 * the same trade `useAdvisorSummary` makes, and its header states it the same way. A human
 * pressing "Try again" is an honest replacement for three automatic triggers; three
 * automatic triggers during a brownout are not a replacement for anything.
 *
 * **What that costs, stated plainly:** a failed fetch stays failed until the reader retries
 * or reloads, and a SUCCEEDED one is no longer refreshed by focus or reconnect either. The
 * second is close to free here: this answer changes only when a family connects or
 * disconnects the firm. `staleTime` still does its job on the success path, where
 * `refetchOnMount` remains live — remounting the page after 5 minutes re-reads.
 */
export function useAdvisorClients(options?: { enabled?: boolean }): UseQueryResult<AdvisorClientsResponse> {
  const token = useAccessToken();
  // Subscribed so a change of acting firm re-keys and refetches — see `advisorActingFirm.ts`.
  useActingFirmId();
  return useQuery({
    // The acting firm belongs in the key as well as the URL (steward-app#983): one cache
    // entry shared across firms would serve firm A's roster to a tab acting as firm B, which
    // is the whole failure this issue closes. `queryKey[0]` stays `'advisor-clients'` because
    // `WorkspaceProvider`'s reset keep-list matches on it.
    queryKey: [...ADVISOR_CLIENTS_QUERY_KEY, actingFirmId()],
    queryFn: ({ queryKey }) =>
      createApiClient(token!).get<AdvisorClientsResponse>(`/advisor/clients${actingFirmQuery(queryKey[1])}`),
    // `enabled` is a cost gate only (`ClientProfile.tsx` reads this for a client's page and
    // not a prospect's), never an authorization boundary: the façade decides what is listed.
    enabled: !!token && (options?.enabled ?? true),
    staleTime: ADVISOR_CLIENTS_STALE_TIME_MS,
    // All three, and none of them is redundant — see the ⚠️ above for which trigger each
    // one closes. Removing any single one puts the fan-out back on that trigger.
    retry: false,
    retryOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });
}
